import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Select,
  HStack,
  Text,
  Spinner,
  useToast,
  Button,
  Flex,
  Spacer,
} from '@chakra-ui/react';
import { FiRefreshCw, FiClock } from 'react-icons/fi';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

const API_BASE = '/api/v1';

function MonitorHistory() {
  const [monitors, setMonitors] = useState([]);
  const [history, setHistory] = useState([]);
  const [selectedMonitor, setSelectedMonitor] = useState('');
  const [loading, setLoading] = useState(true);
  const toast = useToast();

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Token ${localStorage.getItem('token')}`,
  });

  const fetchMonitors = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/monitors/`, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        const monitorList = Array.isArray(data) ? data : data.results || [];
        setMonitors(monitorList);
        if (monitorList.length > 0 && !selectedMonitor) {
          setSelectedMonitor(monitorList[0].id.toString());
        }
      }
    } catch (e) {
      toast({ title: 'Error fetching monitors', status: 'error', duration: 3000 });
    }
  }, [toast, selectedMonitor]);

  const fetchHistory = useCallback(async () => {
    if (!selectedMonitor) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/monitors/${selectedMonitor}/history/`, { 
        headers: getAuthHeaders() 
      });
      if (response.ok) {
        const data = await response.json();
        setHistory(Array.isArray(data) ? data : data.results || []);
      }
    } catch (e) {
      toast({ title: 'Error fetching history', status: 'error', duration: 3000 });
    }
    setLoading(false);
  }, [selectedMonitor, toast]);

  useEffect(() => {
    fetchMonitors();
  }, []);

  useEffect(() => {
    if (selectedMonitor) {
      fetchHistory();
    }
  }, [selectedMonitor, fetchHistory]);

  const handleMonitorChange = (e) => {
    setSelectedMonitor(e.target.value);
  };

  const getStatusBadge = (status) => {
    if (status >= 200 && status < 400) {
      return <Badge colorScheme="green">{status}</Badge>;
    }
    return <Badge colorScheme="red">{status}</Badge>;
  };

  const chartData = history.slice(0, 50).reverse().map((h, index) => ({
    name: new Date(h.created_at).toLocaleTimeString(),
    responseTime: h.response_time,
    status: h.status,
    index,
  }));

  const selectedMonitorData = monitors.find(m => m.id.toString() === selectedMonitor);

  return (
    <Box>
      <Flex align="center" mb={6}>
        <Heading size="lg">Monitor History</Heading>
        <Spacer />
        <HStack spacing={4}>
          <Select
            value={selectedMonitor}
            onChange={handleMonitorChange}
            maxW="400px"
            bg="white"
          >
            {monitors.map(m => (
              <option key={m.id} value={m.id}>{m.url}</option>
            ))}
          </Select>
          <Button leftIcon={<FiRefreshCw />} onClick={fetchHistory} isLoading={loading}>
            Refresh
          </Button>
        </HStack>
      </Flex>

      {selectedMonitorData && (
        <Box bg="white" p={6} borderRadius="lg" boxShadow="md" mb={6}>
          <Heading size="md" mb={4}>Response Time Chart</Heading>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis label={{ value: 'Response Time (s)', angle: -90, position: 'insideLeft' }} />
                <Tooltip />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="responseTime" 
                  stroke="#38A169" 
                  strokeWidth={2}
                  dot={{ fill: '#38A169' }}
                  name="Response Time (s)"
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <Text color="gray.500" textAlign="center" py={8}>
              No history data available for this monitor
            </Text>
          )}
        </Box>
      )}

      {/* History Table */}
      <Box bg="white" p={6} borderRadius="lg" boxShadow="md">
        <Heading size="md" mb={4}>Recent Checks</Heading>
        {loading ? (
          <Flex justify="center" py={8}>
            <Spinner size="lg" color="green.500" />
          </Flex>
        ) : (
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>Timestamp</Th>
                <Th>Status Code</Th>
                <Th>Response Time</Th>
              </Tr>
            </Thead>
            <Tbody>
              {history.map(entry => (
                <Tr key={entry.id}>
                  <Td>{new Date(entry.created_at).toLocaleString()}</Td>
                  <Td>{getStatusBadge(entry.status)}</Td>
                  <Td>
                    <HStack>
                      <FiClock />
                      <Text>{entry.response_time ? `${entry.response_time.toFixed(3)}s` : 'N/A'}</Text>
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}

        {history.length === 0 && !loading && (
          <Text textAlign="center" py={8} color="gray.500">
            No history data available for this monitor
          </Text>
        )}
      </Box>
    </Box>
  );
}

export default MonitorHistory;
